import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Switch } from "@headlessui/react";
import { getUserFromStorage } from "../../../utils/Auth";
import { ReactComponent as EmailIcon } from "../../../assets/images/icons/outline-email.svg";
import { ReactComponent as GoogleIcon } from "../../../assets/images/icons/outline-google.svg";
import { ReactComponent as AppleIcon } from "../../../assets/images/icons/outline-apple.svg";
import {
  allowLoginProvider,
  disallowLoginProvider,
  getUserAuthDetails,
} from "../../../redux/auth/authActions";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function ManageSocialLogin() {
  const user = getUserFromStorage();
  const dispatch = useDispatch();
  const [enabledApprove] = useState(true);
  const [enabledGoogleApprove, setEnabledGoogleApprove] = useState(false);
  const [enabledAppleApprove, setEnabledAppleApprove] = useState(false);
  const { userDetails } = useSelector((state) => state.auth);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // useEffect(() => {
  //   if (userDetails && userDetails?.providers_allowed) {
  //     setEnabledGoogleApprove(
  //       userDetails?.providers_allowed.includes("google")
  //     );
  //     setEnabledAppleApprove(userDetails?.providers_allowed.includes("apple"));
  //   }
  // }, [userDetails]);

  const normalizeProvidersAllowed = (providers) => {
    if (Array.isArray(providers)) return providers;

    if (providers && typeof providers === "object") {
      return Object.values(providers);
    }

    return [];
  };

  useEffect(() => {
    if (userDetails && userDetails?.providers_allowed) {
      const providers = normalizeProvidersAllowed(
        userDetails?.providers_allowed
      );

      setEnabledGoogleApprove(providers.includes("google"));
      setEnabledAppleApprove(providers.includes("apple"));
    }
  }, [userDetails]);

  const handleApprove = async (provider) => {
    try {
      await dispatch(
        allowLoginProvider({ provider_name: provider, user_id: user?.id })
      );
      dispatch(getUserAuthDetails());
    } catch (error) {
      console.log(error);
    }
  };
  const handleUnapprove = async (provider) => {
    try {
      await dispatch(
        disallowLoginProvider({ provider_name: provider, user_id: user?.id })
      );
      dispatch(getUserAuthDetails());
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div data-test="">
      <div className="justify-center items-center mx-auto w-full">
        <form>
          <div className="flex w-full bg-white dark:bg-[#010101] sm:w-auto items-center justify-between mb-4 sm:mb-6 gap-5 px-2 sm:px-8 py-2 sm:py-4 rounded-lg shadow-[-7px_8px_50px_0px_#0000000A,7px_8px_50px_0px_#0000000A]">
            <label className="text-sm mb-2 flex items-center gap-3">
              <EmailIcon className="w-[32px] h-[32px]" />
              <div>
                <p className="text-[#151515] dark:text-white">
                  Email and password
                </p>
                <span className="text-[#344B67] dark:text-[#BCBCBC] text-xs">
                  {userDetails?.email ?? "Enable login with email"}
                </span>
              </div>
            </label>
            <Switch.Group as="div" className="flex">
              <Switch
                checked={enabledApprove}
                disabled
                className={classNames(
                  enabledApprove ? "bg-primary" : "bg-gray-200",
                  "relative inline-flex mt-1 h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none "
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    enabledApprove ? "translate-x-5" : "translate-x-0",
                    "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white dark:bg-[#121212] shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </Switch>
            </Switch.Group>
          </div>

          <div className="flex w-full bg-white dark:bg-[#010101] sm:w-auto items-center justify-between mb-4 sm:mb-6 gap-5 px-2 sm:px-8 py-2 sm:py-4 rounded-lg shadow-[-7px_8px_50px_0px_#0000000A,7px_8px_50px_0px_#0000000A]">
            <label className="text-sm mb-2 flex items-center gap-3">
              <GoogleIcon className="w-[32px] h-[32px]" />
              <div>
                <p className="text-[#151515] dark:text-white">Google</p>
                <span className="text-[#344B67] dark:text-[#BCBCBC] text-xs">
                  Connect your Google account
                </span>
              </div>
            </label>
            <Switch.Group as="div" className="flex">
              <Switch
                checked={enabledGoogleApprove}
                onChange={(e) => {
                  setEnabledGoogleApprove(e);
                  enabledGoogleApprove
                    ? handleUnapprove("google")
                    : handleApprove("google");
                }}
                className={classNames(
                  enabledGoogleApprove ? "bg-primary" : "bg-gray-200",
                  "relative inline-flex mt-1 h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none "
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    enabledGoogleApprove ? "translate-x-5" : "translate-x-0",
                    "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white dark:bg-[#121212] shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </Switch>
            </Switch.Group>
          </div>

          <div className="flex w-full bg-white dark:bg-[#010101] sm:w-auto items-center justify-between mb-4 sm:mb-6 gap-5 px-2 sm:px-8 py-2 sm:py-4 rounded-lg shadow-[-7px_8px_50px_0px_#0000000A,7px_8px_50px_0px_#0000000A]">
            <label className="text-sm mb-2 flex items-center gap-3">
              <AppleIcon className="w-[32px] h-[32px]" />
              <div>
                <p className="text-[#151515] dark:text-white">Apple</p>
                <span className="text-[#344B67] dark:text-[#BCBCBC] text-xs">
                  Connect your Apple account
                </span>
              </div>
            </label>
            <Switch.Group as="div" className="flex">
              <Switch
                checked={enabledAppleApprove}
                onChange={(e) => {
                  setEnabledAppleApprove(e);
                  enabledAppleApprove
                    ? handleUnapprove("apple")
                    : handleApprove("apple");
                }}
                className={classNames(
                  enabledAppleApprove ? "bg-primary" : "bg-gray-200",
                  "relative inline-flex mt-1 h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none "
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    enabledAppleApprove ? "translate-x-5" : "translate-x-0",
                    "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white dark:bg-[#121212] shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </Switch>
            </Switch.Group>
          </div>
        </form>
      </div>
    </div>
  );
}
